'use client';

import {
  Box,
  Combobox,
  chakra,
  Flex,
  HStack,
  Portal,
  Select,
  Text,
  createListCollection,
  type FlexProps,
} from '@chakra-ui/react';
import { ChevronDown } from 'lucide-react';
import {
  forwardRef,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type ButtonHTMLAttributes,
} from 'react';
import { useVoicePageControl, voiceSelectOption } from '@/lib/voice/page-controls';
import { Tooltip } from '@/components/ui/tooltip';
import { useAppRailOverlay } from '@/components/core/app-layout/AppRailContext';
import { FILTER_CONTROL_STYLE } from './ui/FilterPill';

export interface InlineFilterOption {
  value: string;
  label: string;
  group?: string;
  metaLabel?: string | number;
}

export type InlineFilterMode = 'auto' | 'chips' | 'select';

const PILL_H = '38px';
const PILL_PX = '3';
const PILL_FONT_SIZE = '0.9375rem';
const COMPACT_PILL_H = '36px';
const COMPACT_PILL_PX = '2.5';
const COMPACT_PILL_FONT_SIZE = '0.8125rem';
export const FILTER_MENU_CONTENT_PROPS = {
  bg: 'ui.surface.card',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'ui.border.subtle',
  borderRadius: '10px',
  boxShadow: 'none',
  backdropFilter: 'blur(22px)',
  css: { WebkitBackdropFilter: 'blur(22px)' },
  p: '0.5',
} as const;
export const FILTER_MENU_ITEM_PROPS = {
  minH: '32px',
  px: '2.5',
  py: '1',
  borderRadius: '8px',
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  color: 'ui.text.secondary',
  fontSize: COMPACT_PILL_FONT_SIZE,
  _hover: { bg: 'ui.surface.input', color: 'ui.text.primary' },
  _highlighted: { bg: 'ui.surface.input', color: 'ui.text.primary' },
  _focus: { boxShadow: 'none', outline: 'none' },
} as const;

interface UnifiedFilterBarProps extends Omit<FlexProps, 'children'> {
  children: ReactNode;
}

export function UnifiedFilterBar({ children, ...props }: UnifiedFilterBarProps) {
  return (
    <Flex
      data-section="unifiedFilterBar"
      align="center"
      gap="1.5"
      w="full"
      minW={0}
      maxW="full"
      flexWrap="nowrap"
      overflowX="auto"
      css={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      {...props}
    >
      {children}
    </Flex>
  );
}

interface InlineFilterLabelProps {
  label: string;
  helpText?: string;
  compact?: boolean;
}

export function InlineFilterLabel({ label, helpText, compact = true }: InlineFilterLabelProps) {
  return (
    <HStack gap="1" align="center" flexShrink={0}>
      <Text
        fontSize={compact ? '0.75rem' : '0.6875rem'}
        fontWeight={compact ? '600' : '650'}
        color="ui.text.tertiary"
        textTransform={compact ? 'none' : 'uppercase'}
        letterSpacing={compact ? 'normal' : '0.08em'}
      >
        {label}
      </Text>
      {helpText ? (
        <Tooltip
          content={helpText}
          openDelay={300}
          portalled
          contentProps={
            {
              layerStyle: 'surfacePopover',
              borderRadius: 'glassInput',
              maxW: '300px',
              px: '3',
              py: '2',
              fontSize: 'xs',
              color: 'ui.text.primary',
              lineHeight: '1.5',
            } as object
          }
        >
          <Box
            as="span"
            w="14px"
            h="14px"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="full"
            border="1px solid"
            borderColor="ui.border.subtle"
            color="ui.text.tertiary"
            fontSize="0.6875rem"
            fontWeight="700"
            lineHeight="1"
            cursor="help"
            flexShrink={0}
            _hover={{ color: 'ui.text.secondary', borderColor: 'ui.border.default' }}
          >
            i
          </Box>
        </Tooltip>
      ) : null}
    </HStack>
  );
}

interface FilterChipButtonProps {
  label: string;
  group?: string;
  metaLabel?: string | number;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  compact?: boolean;
}

export function FilterChipButton({
  label,
  metaLabel,
  isActive = false,
  disabled = false,
  onClick,
  compact = true,
}: FilterChipButtonProps) {
  return (
    <Box
      as="button"
      layerStyle="filterPill"
      {...(compact ? FILTER_CONTROL_STYLE : {})}
      px={compact ? COMPACT_PILL_PX : PILL_PX}
      h={compact ? COMPACT_PILL_H : PILL_H}
      maxW={{ base: '148px', md: compact ? '200px' : '220px' }}
      minW={0}
      flexShrink={0}
      display="inline-flex"
      alignItems="center"
      gap="1.5"
      borderRadius={compact ? '10px' : undefined}
      borderColor={isActive ? 'ui.border.default' : 'ui.border.subtle'}
      bg={isActive ? 'ui.surface.input' : 'transparent'}
      color={isActive ? 'ui.text.primary' : 'ui.text.secondary'}
      fontSize={compact ? COMPACT_PILL_FONT_SIZE : PILL_FONT_SIZE}
      fontWeight={isActive ? '600' : '500'}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.45 : 1}
      _hover={
        disabled
          ? undefined
          : {
              color: 'ui.text.primary',
              bg: isActive ? 'ui.surface.input' : 'ui.surface.selected',
              borderColor: 'ui.border.default',
            }
      }
      _focusVisible={{ outline: 'none', boxShadow: 'none' }}
      transition="background-color 0.12s, border-color 0.12s, color 0.12s"
      onClick={disabled ? undefined : onClick}
    >
      <Text as="span" truncate minW={0}>
        {label}
      </Text>
      {metaLabel !== undefined && metaLabel !== null ? (
        <Text
          as="span"
          fontSize="0.75rem"
          color={isActive ? 'ui.text.primary' : 'ui.text.tertiary'}
          fontWeight="500"
          flexShrink={0}
        >
          {metaLabel}
        </Text>
      ) : null}
    </Box>
  );
}

export interface InlineFilterGroupProps {
  label: string;
  options: InlineFilterOption[];
  value: string;
  onChange: (value: string) => void;
  helpText?: string;
  disabled?: boolean;
  placeholder?: string;
  // mode and maxChipOptions kept for API compatibility but chips mode is removed
  mode?: InlineFilterMode;
  maxChipOptions?: number;
  minSelectWidth?: string | { base?: string; md?: string };
  maxSelectWidth?: string | { base?: string; md?: string };
  hideLabelOnMobile?: boolean;
  hideLabel?: boolean;
  compact?: boolean;
  portalled?: boolean;
  searchable?: boolean;
  /** Bound rendered matches without truncating the searchable inventory. */
  maxVisibleOptions?: number;
}

type FilterTriggerProps = Omit<InlineFilterGroupProps, 'options' | 'value' | 'onChange'> &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    activeLabel: string;
    isFiltered: boolean;
    secondaryLabel?: string;
  };

export const InlineFilterTrigger = forwardRef<HTMLButtonElement, FilterTriggerProps>(
  function InlineFilterTrigger(
    {
      label,
      activeLabel,
      helpText,
      disabled,
      minSelectWidth,
      maxSelectWidth = '220px',
      hideLabelOnMobile,
      hideLabel = true,
      compact = true,
      searchable,
      isFiltered,
      secondaryLabel,
      ...buttonProps
    },
    ref
  ) {
    return (
      <chakra.button
        ref={ref}
        {...buttonProps}
        disabled={disabled}
        aria-label={`${label}: ${activeLabel}${secondaryLabel ? `, ${secondaryLabel}` : ''}`}
        title={buttonProps.title ?? activeLabel}
        layerStyle="filterPill"
        {...FILTER_CONTROL_STYLE}
        h={compact ? COMPACT_PILL_H : PILL_H}
        minW={minSelectWidth ?? 'auto'}
        w="auto"
        maxW={maxSelectWidth}
        px={compact ? COMPACT_PILL_PX : PILL_PX}
        display="inline-flex"
        alignItems="center"
        gap={compact ? '1.5' : '2'}
        overflow="hidden"
        borderRadius={compact ? '10px' : undefined}
        borderColor={isFiltered ? 'ui.border.default' : 'ui.border.subtle'}
        bg={isFiltered ? 'ui.surface.input' : 'transparent'}
        color={isFiltered ? 'ui.text.primary' : 'ui.text.secondary'}
        fontSize={compact ? COMPACT_PILL_FONT_SIZE : PILL_FONT_SIZE}
        fontWeight={isFiltered ? '600' : '500'}
        cursor={disabled ? 'not-allowed' : 'pointer'}
        opacity={disabled ? 0.45 : 1}
        _hover={
          disabled
            ? undefined
            : {
                color: 'ui.text.primary',
                bg: isFiltered ? 'ui.surface.input' : 'ui.surface.selected',
                borderColor: 'ui.border.default',
              }
        }
        _focus={{ outline: 'none' }}
        _focusVisible={{
          borderColor: 'ui.border.default',
          boxShadow: '0 0 0 2px var(--chakra-colors-ui-border-default)',
        }}
        transition="background-color 0.12s, border-color 0.12s, color 0.12s"
        whiteSpace="nowrap"
      >
        <Box
          as="span"
          display={
            hideLabel
              ? 'none'
              : hideLabelOnMobile
                ? { base: 'none', md: 'inline-flex' }
                : 'inline-flex'
          }
          alignItems="center"
        >
          <InlineFilterLabel label={label} helpText={helpText} compact={compact} />
        </Box>
        <Box
          as="span"
          display={
            hideLabel
              ? 'none'
              : hideLabelOnMobile
                ? { base: 'none', md: 'inline-flex' }
                : 'inline-flex'
          }
          w="1px"
          h="14px"
          bg="ui.border.subtle"
          flexShrink={0}
          aria-hidden="true"
        />
        <Text as="span" truncate minW={0} flex="1" lineHeight="inherit">
          {activeLabel}
        </Text>
        {secondaryLabel ? (
          <Text as="span" fontSize="10px" color="ui.text.tertiary" flexShrink={0}>
            {secondaryLabel}
          </Text>
        ) : null}
        <Box as="span" display="inline-flex" opacity={0.45} flexShrink={0} ml="auto">
          <ChevronDown size={10} />
        </Box>
      </chakra.button>
    );
  }
);

export function InlineFilterGroup(props: InlineFilterGroupProps) {
  return props.searchable ? (
    <SearchableInlineFilterGroup {...props} />
  ) : (
    <StandardInlineFilterGroup {...props} />
  );
}

function StandardInlineFilterGroup({
  label,
  options,
  value,
  onChange,
  helpText,
  disabled = false,
  placeholder,
  minSelectWidth,
  maxSelectWidth,
  hideLabelOnMobile = false,
  hideLabel = true,
  compact = true,
  portalled = true,
}: InlineFilterGroupProps) {
  const popupOwner = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const onRailOverlayChange = useAppRailOverlay();
  const voiceRef = useVoicePageControl({
    label,
    kind: 'select',
    value,
    options,
    disabled,
    apply: requested => {
      const next = voiceSelectOption(options, requested);
      onChange(next);
      return next;
    },
  });
  const collection = createListCollection({ items: options });

  const activeLabel =
    options.find(o => o.value === value)?.label ?? placeholder ?? options[0]?.label ?? 'All';

  const isFiltered = Boolean(value);

  return (
    <Select.Root
      ref={voiceRef}
      data-filter-control={popupOwner}
      collection={collection}
      value={value ? [value] : ['']}
      onValueChange={event => onChange(event.value[0] || '')}
      onOpenChange={({ open }) =>
        onRailOverlayChange({
          open:
            open && Boolean(triggerRef.current?.closest('[data-section="railSectionControls"]')),
        })
      }
      size="xs"
      display="inline-flex"
      width="auto"
      flexShrink={0}
      positioning={{ sameWidth: false, placement: 'bottom-start' }}
      disabled={disabled}
    >
      <Select.HiddenSelect />
      <Select.Trigger asChild disabled={disabled}>
        <InlineFilterTrigger
          ref={triggerRef}
          label={label}
          activeLabel={activeLabel}
          helpText={helpText}
          disabled={disabled}
          minSelectWidth={minSelectWidth}
          maxSelectWidth={maxSelectWidth}
          hideLabelOnMobile={hideLabelOnMobile}
          hideLabel={hideLabel}
          compact={compact}
          isFiltered={isFiltered}
        />
      </Select.Trigger>
      <Portal disabled={!portalled}>
        <Select.Positioner data-filter-popup={popupOwner}>
          <Select.Content
            minW="200px"
            maxW="calc(100vw - 32px)"
            {...FILTER_MENU_CONTENT_PROPS}
            outline="none"
          >
            {Array.from(new Set(collection.items.map(item => item.group))).map(group => {
              const items = collection.items
                .filter(item => item.group === group)
                .map(item => (
                  <Select.Item key={item.value} item={item} {...FILTER_MENU_ITEM_PROPS}>
                    <Text fontSize="0.875rem" color="inherit" truncate maxW="full">
                      {item.label}
                    </Text>
                  </Select.Item>
                ));
              return group ? (
                <Select.ItemGroup key={group}>
                  <Select.ItemGroupLabel px="2.5" py="1.5" fontSize="xs" color="ui.text.tertiary">
                    {group}
                  </Select.ItemGroupLabel>
                  {items}
                </Select.ItemGroup>
              ) : (
                items
              );
            })}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
}

function SearchableInlineFilterGroup({
  label,
  options,
  value,
  onChange,
  helpText,
  disabled = false,
  placeholder,
  minSelectWidth,
  maxSelectWidth,
  hideLabelOnMobile = false,
  hideLabel = true,
  compact = true,
  portalled = true,
  maxVisibleOptions,
}: InlineFilterGroupProps) {
  const popupOwner = useId();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const onRailOverlayChange = useAppRailOverlay();
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const voiceRef = useVoicePageControl({
    label,
    kind: 'select',
    value,
    options,
    disabled,
    apply: requested => {
      const next = voiceSelectOption(options, requested);
      onChange(next);
      return next;
    },
  });
  const matches = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return query ? options.filter(option => option.label.toLocaleLowerCase().includes(query)) : options;
  }, [options, search]);
  const collection = useMemo(() => createListCollection({
    items: maxVisibleOptions ? matches.slice(0, maxVisibleOptions) : matches,
  }), [matches, maxVisibleOptions]);
  const groups = useMemo(() => {
    const result = new Map<string, InlineFilterOption[]>();
    for (const item of collection.items) {
      const group = item.group || '';
      const items = result.get(group) || [];
      items.push(item);
      result.set(group, items);
    }
    return result;
  }, [collection]);
  const activeLabel =
    options.find(option => option.value === value)?.label ??
    placeholder ??
    options[0]?.label ??
    'All';

  return (
    <Combobox.Root
      ref={voiceRef}
      data-filter-control={popupOwner}
      collection={collection}
      value={[value]}
      inputValue={search}
      open={open}
      onInputValueChange={event => setSearch(event.inputValue)}
      onValueChange={event => {
        onChange(event.value[0] || '');
        requestAnimationFrame(() => triggerRef.current?.focus());
      }}
      onOpenChange={event => {
        setOpen(event.open);
        onRailOverlayChange({
          open:
            event.open &&
            Boolean(triggerRef.current?.closest('[data-section="railSectionControls"]')),
        });
        if (event.open) {
          setSearch('');
          requestAnimationFrame(() => inputRef.current?.focus());
        }
      }}
      selectionBehavior="preserve"
      openOnClick={false}
      disabled={disabled}
      size="xs"
      display="inline-flex"
      flexDirection="row"
      gap={0}
      width="auto"
      flexShrink={0}
      positioning={{ sameWidth: false, placement: 'bottom-start' }}
    >
      <Combobox.Trigger asChild position="static" inset="auto">
        <InlineFilterTrigger
          ref={triggerRef}
          label={label}
          activeLabel={activeLabel}
          helpText={helpText}
          disabled={disabled}
          minSelectWidth={minSelectWidth}
          maxSelectWidth={maxSelectWidth}
          hideLabelOnMobile={hideLabelOnMobile}
          hideLabel={hideLabel}
          compact={compact}
          isFiltered={Boolean(value)}
          searchable
        />
      </Combobox.Trigger>
      <Portal disabled={!portalled}>
        <Combobox.Positioner data-filter-popup={popupOwner} display={open ? 'block' : 'none'} zIndex="dropdown">
          <Box {...FILTER_MENU_CONTENT_PROPS} w="340px" maxW="calc(100vw - 32px)">
            <Combobox.Input
              ref={inputRef}
              aria-label={`Search ${label.toLowerCase()}`}
              placeholder={`Search ${label.toLowerCase()}…`}
              h="36px"
              px="2.5"
              mb="0.5"
              bg="ui.surface.input"
              color="ui.text.primary"
              borderRadius="8px"
              borderColor="ui.border.subtle"
              fontSize="sm"
              onKeyDown={event => {
                if (event.key === 'Escape')
                  requestAnimationFrame(() => triggerRef.current?.focus());
              }}
            />
            {maxVisibleOptions && matches.length > maxVisibleOptions ? (
              <Text px="2.5" py="1" fontSize="xs" color="ui.text.tertiary">
                Showing {maxVisibleOptions} of {matches.length.toLocaleString()} matches. Search to narrow results.
              </Text>
            ) : null}
            <Combobox.Content
              bg="transparent"
              p={0}
              border={0}
              boxShadow="none"
              maxH="320px"
              overflowY="auto"
              outline="none"
            >
              {Array.from(groups, ([group, items]) => {
                const rows = items.map(item => (
                  <Combobox.Item key={item.value} item={item} {...FILTER_MENU_ITEM_PROPS}>
                    <Text
                      fontSize="0.875rem"
                      color="inherit"
                      title={item.label}
                      truncate
                      maxW="full"
                    >
                      {item.label}
                    </Text>
                  </Combobox.Item>
                ));
                return group ? (
                  <Combobox.ItemGroup key={group}>
                    <Combobox.ItemGroupLabel
                      px="2.5"
                      py="1.5"
                      fontSize="xs"
                      color="ui.text.tertiary"
                    >
                      {group}
                    </Combobox.ItemGroupLabel>
                    {rows}
                  </Combobox.ItemGroup>
                ) : (
                  rows
                );
              })}
              <Combobox.Empty px="2.5" py="2" fontSize="sm" color="ui.text.tertiary">
                No matches
              </Combobox.Empty>
            </Combobox.Content>
          </Box>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
}

interface FilterStatPillProps {
  label: string;
  value: string | number;
}

export function FilterStatPill({ label, value }: FilterStatPillProps) {
  return (
    <Text
      fontSize="xs"
      color="ui.text.tertiary"
      whiteSpace="nowrap"
      flexShrink={0}
      aria-label={`${label}: ${value}`}
    >
      {value} {label.toLowerCase() === 'showing' ? 'shown' : label.toLowerCase()}
    </Text>
  );
}
